import { HEAT_STYLES, formatHeatPct, heatLevel } from '../utils/portfolioHeat'
import Tooltip from './Tooltip'
import { DASHBOARD_GLOSSARY } from '../config/glossary'

interface PortfolioHeatGaugeProps {
  /** Total heat, as a fraction of the account (0.05 = 5%). */
  heatPct: number
  /** The configured limit, same units. Never hardcoded: pass max_heat_pct. */
  maxHeatPct: number
  /** "stacked" for a stat tile, "inline" for a one-line rail. */
  variant?: 'stacked' | 'inline'
}

/**
 * Portfolio heat coloured by how close it sits to the limit, with a thin bar
 * against that limit and a one-word reading.
 */
const PortfolioHeatGauge = ({ heatPct, maxHeatPct, variant = 'stacked' }: PortfolioHeatGaugeProps) => {
  const level = heatLevel(heatPct, maxHeatPct)
  const style = HEAT_STYLES[level]
  const fill = maxHeatPct > 0 ? Math.min((heatPct / maxHeatPct) * 100, 100) : 100

  const bar = (
    <div
      role="progressbar"
      aria-label="Portfolio heat against limit"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fill)}
      aria-valuetext={`${formatHeatPct(heatPct)} of ${formatHeatPct(maxHeatPct, 0)} limit, ${style.label}`}
      className={`h-1.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden ${variant === 'inline' ? 'w-32' : 'w-full'}`}
    >
      <div className={`h-full rounded-full transition-all ${style.bar}`} style={{ width: `${fill}%` }} />
    </div>
  )

  const badge = (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${style.badge}`}>
      {level === 'over_limit' ? (
        <Tooltip content={DASHBOARD_GLOSSARY.portfolioHeatOverLimit(formatHeatPct(maxHeatPct, 0))}>
          {style.label}
        </Tooltip>
      ) : (
        style.label
      )}
    </span>
  )

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-3">
        {bar}
        <span className={`text-sm font-bold tabular-nums ${style.text}`}>
          {formatHeatPct(heatPct)} / {formatHeatPct(maxHeatPct, 0)}
        </span>
        {badge}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className={`text-3xl font-bold tabular-nums ${style.text}`}>{formatHeatPct(heatPct)}</span>
        {badge}
      </div>
      <div className="mt-3">{bar}</div>
      <p className="text-sm text-gray-500 mt-1">of {formatHeatPct(maxHeatPct, 0)} limit</p>
    </div>
  )
}

export default PortfolioHeatGauge
